import { marked } from 'marked';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectUserProjectId } from 'core/userContext/selectors';
import {
  ImageHeader,
  TextHeader,
  WhatsAppContentDefinition,
  WhatsAppHeaderDefinition,
} from 'modules/graphql';
import { ArrowBack, Open16, Overflow, Phone16 } from 'ui/icons';
import { Markdown } from 'ui/markdown';

import { TemplateType } from 'constants/templateTypes';
import { TemplateEditViews } from '../../common/types';
import { getTime, shortenLinkInMessage } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useTemplateReviewTakeoverContext } from '../context/TemplateReviewTakeoverContext';
import { useWhatsAppLinkShortening } from '../context/WhatsAppLinkShorteningContext';
import { useWhatsAppPreviewContext } from '../context/WhatsAppPreviewContext';
import { useWhatsAppSenderContext } from '../context/WhatsAppSenderContext';
import { getSelectedSenderLabel } from '../utils';
import {
  chatTick,
  messageBody,
  messageBodyMedia,
  messageBodyWhatsAppMessage,
  messageBodyWhatsAppMessageContainer,
  messageBodyWhatsAppMessageSenderContainer,
  messageBodyWhatsAppMessageTime,
  messageBodyWhatsAppSenderMessage,
  messageContentHeader,
  messageHeaderWhatsApp,
  messageHeaderWhatsAppCenter,
  messageHeaderWhatsAppSide,
  previewFooter,
  senderChatTick,
  whatsAppButton,
  whatsAppSkeletonBarHalfSenderContent,
  whatsAppSkeletonBarSenderContent,
} from './styles';

const StyledMarkdown = styled(Markdown)`
  line-height: 0;
`;

// Preprocessing for passing message content to WA preview to override formatting to render bold content. WA uses *{content}* for bold rendering
// but standard markdown treats that as italics so we need to replace the surrounding * with surrounding __ for bold rendering. This only affects
// preview rendering and WA messages will be sent with * to render on devices correctly.
const whatsAppPreProcessor = (markdown: string) => {
  const whatsAppBoldFormatting = /([^\*]?)\*{1}([^\*]+)\*{1}([^\*]?)/g;

  return markdown.replaceAll(whatsAppBoldFormatting, (_, g1, g2, g3) => `${g1}__${g2}__${g3}`);
};

const renderHeader = (headerObj?: WhatsAppHeaderDefinition) => {
  if ((headerObj as ImageHeader)?.mediaUrl) {
    return (
      <img
        data-test={'whats-app-imageUrl'}
        style={{
          // background: `url(${(headerObj as ImageHeader)?.mediaUrl}) no-repeat`,
          ...messageBodyMedia,
        }}
        src={(headerObj as ImageHeader)?.mediaUrl}
      />
    );
  }
  if ((headerObj as TextHeader)?.text) {
    return <div style={messageContentHeader}>{(headerObj as TextHeader)?.text}</div>;
  }
  return null;
};

export const Message = () => {
  const projectId = useSelector(selectUserProjectId);
  const { isTemplateReviewTakeoverOpen } = useTemplateReviewTakeoverContext();

  const { liveTemplate } = useEditableFieldsContext();
  const { locale, view } = useQueryParamContext();

  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  const { senders } = useWhatsAppSenderContext();
  const { whatsAppHeader, whatsAppMessage, whatsAppButtons } = useWhatsAppPreviewContext();
  const { isWhatsAppLinkShorteningEnabled, shortlinkLength, defaultWhatsAppLinkShorteningDomain } =
    useWhatsAppLinkShortening();

  // note: this date and time appear in a localized preview, so we specify the locale of the template
  const dateTime = new Date();
  const time = getTime(dateTime, locale);
  const usePopulatedField =
    (view === TemplateEditViews.preview || isTemplateReviewTakeoverOpen) &&
    (!isDataFeedDataEmpty || !isUserDataEmpty);
  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const isUserInitiated = liveTemplate.templateType === TemplateType.UserInitiated;

  const { footer, whatsAppSenderId } = whatsAppData;
  const message = (usePopulatedField ? whatsAppMessage : whatsAppData.message) || '';
  const header = (usePopulatedField ? whatsAppHeader : whatsAppData.header) || {};
  const buttons = (usePopulatedField ? whatsAppButtons : whatsAppData.buttons) || [];

  const shortenLinkInTextContent = (textContent: string = '') => {
    return (
      shortenLinkInMessage(
        textContent,
        whatsAppData?.trackingDomain ?? defaultWhatsAppLinkShorteningDomain ?? '',
        projectId,
        shortlinkLength,
        isWhatsAppLinkShorteningEnabled,
      ) || ''
    );
  };

  const messageMaybeWithShortenLink = shortenLinkInTextContent(message);
  const headerMaybeWithShortenLink = {
    ...header,
    ...((header as TextHeader)?.text
      ? { text: shortenLinkInTextContent((header as TextHeader)?.text) }
      : {}),
  };
  const footerMaybeWithShortenLink = shortenLinkInTextContent(footer);

  const senderLabel = getSelectedSenderLabel(senders, whatsAppSenderId);

  useEffect(() => {
    marked.use({ hooks: { preprocess: whatsAppPreProcessor } });

    return () => {
      marked.use();
    };
  }, []);

  return (
    <>
      <div style={messageHeaderWhatsApp}>
        <div style={messageHeaderWhatsAppSide}>
          <ArrowBack color={`#FFFFFF`} height={22} width={22} />
        </div>
        <div style={messageHeaderWhatsAppCenter}>{senderLabel}</div>
        <div style={messageHeaderWhatsAppSide}>
          <Phone16 color={`#FFFFFF`} height={22} width={22} />
        </div>
        <div style={messageHeaderWhatsAppSide}>
          <Overflow color={`#FFFFFF`} height={22} width={22} />
        </div>
      </div>
      <div style={messageBody}>
        {isUserInitiated && (
          <div style={messageBodyWhatsAppMessageSenderContainer}>
            <div data-test="whats-app-message" style={messageBodyWhatsAppSenderMessage}>
              <div style={whatsAppSkeletonBarSenderContent} />
              <div style={whatsAppSkeletonBarSenderContent} />
              <div style={whatsAppSkeletonBarHalfSenderContent} />
            </div>
            <div style={senderChatTick} />
          </div>
        )}
        <div style={messageBodyWhatsAppMessageContainer}>
          <div style={chatTick} />
          <div data-test="whats-app-message" style={messageBodyWhatsAppMessage}>
            {renderHeader(headerMaybeWithShortenLink)}
            <StyledMarkdown
              id="whats-app-preview-body"
              markdownText={messageMaybeWithShortenLink.trim()}
            />
            {footer && <div style={previewFooter}>{footerMaybeWithShortenLink}</div>}
            <div style={messageBodyWhatsAppMessageTime}>{time}</div>
          </div>
        </div>
        {buttons?.map((button, idx) => {
          return (
            <div
              data-test={`whats-app-button-${idx}`}
              key={`${button?.buttonText}-${idx}`}
              style={whatsAppButton}
            >
              {'url' in button && <Open16 color="#0096DE" height={14} width={14} />}
              {'phoneNumber' in button && <Phone16 height={14} width={14} color="#0096DE" />}
              {button.buttonText}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Message;
